import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';

const SIDEBAR_WIDTH = 320;
const TOGGLE_WIDTH = 40;
const TOGGLE_HEIGHT = 80;
const CONTENT_TOP = 95; // Where scrollable content starts
const CONTENT_BOTTOM = GAME_HEIGHT - 20; // Bottom padding
const SCROLL_ZONE_HEIGHT = CONTENT_BOTTOM - CONTENT_TOP;

export class HelpSidebar {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private toggleButton: Phaser.GameObjects.Container;
  private scrollContent!: Phaser.GameObjects.Container;
  private contentHeight: number = 0;
  private scrollY: number = 0;
  private isOpen: boolean = false;
  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private scrollStartY: number = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = this.createSidebar();
    this.toggleButton = this.createToggleButton();
  }

  private createSidebar(): Phaser.GameObjects.Container {
    // Start slightly off-screen to hide the border (stroke extends beyond fill)
    const container = this.scene.add.container(GAME_WIDTH + 4, 0);

    // Background panel
    const bg = this.scene.add.rectangle(
      SIDEBAR_WIDTH / 2,
      GAME_HEIGHT / 2,
      SIDEBAR_WIDTH,
      GAME_HEIGHT,
      PERSIAN_THEME.colors.tableCenter,
      0.98
    );
    bg.setStrokeStyle(2, PERSIAN_THEME.colors.gold);

    container.add(bg);

    // Farsi title
    const farsiTitle = this.scene.add.text(SIDEBAR_WIDTH / 2, 30, PERSIAN_THEME.farsi.rules, {
      fontFamily: PERSIAN_THEME.fonts.farsi,
      fontSize: '24px',
      color: PERSIAN_THEME.hex.gold,
    });
    farsiTitle.setOrigin(0.5);
    container.add(farsiTitle);

    // English title
    const title = this.scene.add.text(SIDEBAR_WIDTH / 2, 58, 'Game Rules', {
      fontFamily: PERSIAN_THEME.fonts.title,
      fontSize: '18px',
      color: PERSIAN_THEME.hex.textPrimary,
    });
    title.setOrigin(0.5);
    container.add(title);

    // Divider
    const divider = this.scene.add.rectangle(
      SIDEBAR_WIDTH / 2,
      85,
      SIDEBAR_WIDTH - 40,
      1,
      PERSIAN_THEME.colors.bronze,
      0.5
    );
    container.add(divider);

    // Create scrollable content container
    this.scrollContent = this.scene.add.container(0, CONTENT_TOP);

    // Rules content
    const rules = [
      { header: 'Objective', text: 'First team to reach 7 points wins.' },
      {
        header: 'Teams',
        text: `Opposite seats are partners.\n${PERSIAN_THEME.teamNames.A} (South + North) vs ${PERSIAN_THEME.teamNames.B} (West + East)`,
      },
      { header: 'Card Ranking', text: 'A > K > Q > J > 10...2\nWithin each suit: ♠ ♥ ♦ ♣' },
      {
        header: 'Selecting Hâkem',
        text: 'Cards are dealt one at a time.\nFirst player to get an Ace\nbecomes Hâkem for the first game.',
      },
      { header: `Hâkem (${PERSIAN_THEME.farsi.hakem})`, text: 'Selects trump suit after seeing\nfirst 5 cards.' },
      {
        header: 'Following Suit',
        text: 'Must play lead suit if you have it.\nIf void, you may:\n• Play trump to WIN the trick\n• Discard any card (you lose)\nHighest trump always wins.',
      },
      { header: 'Scoring', text: '7+ tricks = 1 point\nKot (7-0 sweep) = 2 points\nHâkem Koti (7-0 vs Hâkem) = 3 pts' },
    ];

    let yPos = 0;
    rules.forEach((rule) => {
      const header = this.scene.add.text(20, yPos, rule.header, {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: PERSIAN_THEME.hex.gold,
        fontStyle: 'bold',
      });
      this.scrollContent.add(header);

      yPos += 18;

      const text = this.scene.add.text(20, yPos, rule.text, {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '12px',
        color: PERSIAN_THEME.hex.textSecondary,
        wordWrap: { width: SIDEBAR_WIDTH - 40 },
        lineSpacing: 3,
      });
      this.scrollContent.add(text);

      yPos += text.height + 15;
    });

    // Farsi terms section
    const termsY = yPos + 5;
    const termsDivider = this.scene.add.rectangle(
      SIDEBAR_WIDTH / 2,
      termsY,
      SIDEBAR_WIDTH - 40,
      1,
      PERSIAN_THEME.colors.bronze,
      0.5
    );
    this.scrollContent.add(termsDivider);

    const termsTitle = this.scene.add.text(SIDEBAR_WIDTH / 2, termsY + 15, 'Persian Terms', {
      fontFamily: PERSIAN_THEME.fonts.body,
      fontSize: '14px',
      color: PERSIAN_THEME.hex.gold,
      fontStyle: 'bold',
    });
    termsTitle.setOrigin(0.5);
    this.scrollContent.add(termsTitle);

    const terms = [
      { farsi: PERSIAN_THEME.farsi.hokm, english: 'Trump', pronunciation: 'hokm' },
      { farsi: PERSIAN_THEME.farsi.hakem, english: 'Dealer/King', pronunciation: 'hah-kem' },
      { farsi: PERSIAN_THEME.farsi.kot, english: 'First 7 tricks sweep', pronunciation: 'kot' },
    ];

    let termY = termsY + 38;
    terms.forEach((term) => {
      const termText = this.scene.add.text(
        SIDEBAR_WIDTH / 2,
        termY,
        `${term.farsi} (${term.pronunciation}) - ${term.english}`,
        {
          fontFamily: PERSIAN_THEME.fonts.farsi,
          fontSize: '13px',
          color: PERSIAN_THEME.hex.textSecondary,
        }
      );
      termText.setOrigin(0.5);
      this.scrollContent.add(termText);
      termY += 22;
    });

    // Calculate total content height
    this.contentHeight = termY + 20;

    // Add scroll content to container
    container.add(this.scrollContent);

    // Create mask for scrollable area - positioned where sidebar will be when OPEN
    // (sidebar moves from GAME_WIDTH+4 to GAME_WIDTH+4-SIDEBAR_WIDTH when opening)
    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(GAME_WIDTH - SIDEBAR_WIDTH + 4, CONTENT_TOP, SIDEBAR_WIDTH, SCROLL_ZONE_HEIGHT);
    const mask = maskShape.createGeometryMask();
    this.scrollContent.setMask(mask);

    // Scroll indicator (shows if content is scrollable)
    if (this.contentHeight > SCROLL_ZONE_HEIGHT) {
      const scrollHint = this.scene.add.text(SIDEBAR_WIDTH / 2, GAME_HEIGHT - 15, '↕ Scroll for more', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '11px',
        color: PERSIAN_THEME.hex.bronze,
      });
      scrollHint.setOrigin(0.5);
      container.add(scrollHint);
    }

    // Setup scroll interaction
    this.setupScrolling(bg);

    container.setDepth(100);
    return container;
  }

  private setupScrolling(bg: Phaser.GameObjects.Rectangle): void {
    bg.setInteractive();

    bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y > CONTENT_TOP && pointer.y < CONTENT_BOTTOM) {
        this.isDragging = true;
        this.dragStartY = pointer.y;
        this.scrollStartY = this.scrollY;
      }
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging && this.isOpen) {
        const deltaY = pointer.y - this.dragStartY;
        this.setScrollPosition(this.scrollStartY + deltaY);
      }
    });

    this.scene.input.on('pointerup', () => {
      this.isDragging = false;
    });

    // Mouse wheel scrolling
    this.scene.input.on(
      'wheel',
      (
        _pointer: Phaser.Input.Pointer,
        _gameObjects: Phaser.GameObjects.GameObject[],
        _deltaX: number,
        deltaY: number
      ) => {
        if (this.isOpen) {
          this.setScrollPosition(this.scrollY - deltaY * 0.5);
        }
      }
    );
  }

  private setScrollPosition(newY: number): void {
    const maxScroll = 0;
    const minScroll = Math.min(0, SCROLL_ZONE_HEIGHT - this.contentHeight);

    this.scrollY = Math.max(minScroll, Math.min(maxScroll, newY));
    this.scrollContent.y = CONTENT_TOP + this.scrollY;
  }

  private createToggleButton(): Phaser.GameObjects.Container {
    // Position at top-right corner, just left of sidebar's starting position
    // Sidebar starts at x = GAME_WIDTH + 4 (hidden), button is adjacent to it
    const container = this.scene.add.container(GAME_WIDTH - TOGGLE_WIDTH + 4, 20);

    // Button background
    const bg = this.scene.add.rectangle(
      TOGGLE_WIDTH / 2,
      TOGGLE_HEIGHT / 2,
      TOGGLE_WIDTH,
      TOGGLE_HEIGHT,
      PERSIAN_THEME.colors.jade,
      0.95
    );
    bg.setStrokeStyle(2, PERSIAN_THEME.colors.gold);

    // Question mark icon
    const icon = this.scene.add.text(TOGGLE_WIDTH / 2, TOGGLE_HEIGHT / 2, '?', {
      fontFamily: PERSIAN_THEME.fonts.title,
      fontSize: '28px',
      color: PERSIAN_THEME.hex.gold,
    });
    icon.setOrigin(0.5);

    container.add([bg, icon]);

    // Make the background interactive (more reliable than container)
    bg.setInteractive({ useHandCursor: true });

    bg.on('pointerover', () => {
      bg.setFillStyle(PERSIAN_THEME.colors.tableCenter);
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(PERSIAN_THEME.colors.jade);
    });

    bg.on('pointerdown', () => {
      this.toggle();
    });

    container.setDepth(101);
    return container;
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    // Animate both together - sidebar and button move the same distance (SIDEBAR_WIDTH)
    this.scene.tweens.add({
      targets: [this.container, this.toggleButton],
      x: '-=' + SIDEBAR_WIDTH,
      duration: 300,
      ease: 'Power2',
    });
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Animate both together - sidebar and button move the same distance (SIDEBAR_WIDTH)
    this.scene.tweens.add({
      targets: [this.container, this.toggleButton],
      x: '+=' + SIDEBAR_WIDTH,
      duration: 300,
      ease: 'Power2',
    });
  }

  destroy(): void {
    this.container.destroy();
    this.toggleButton.destroy();
  }
}
